import { hashString, type WeaponModelSpec } from '@vm/shared';
import { CAMO_BY_ID, type CamoDef } from '../cosmetics';
import { WEAPONS } from '../weapons';
import {
  ACC,
  ACC_D,
  ACC_L,
  BRASS,
  DARK,
  DARK_L,
  EMISSIVE_SLOTS,
  GLOW,
  GLOW_HI,
  LENS,
  LENS_HI,
  LIGHT,
  METAL,
  METAL_D,
  METAL_L,
  ModelBuilder,
  PRI,
  PRI_D,
  RED,
  RUBBER,
  SEC,
  SEC_D,
  SEC_L,
  SLOT_COUNT,
  WHITE,
  type WeaponPartName,
} from './builder';
import { applyCamo, camoActive, mix, tone } from './camo';
import { ARCHETYPE_DESIGN, DESIGNS, type DesignMeta, type V3, type WeaponRig } from './designs';

export type { WeaponPartName } from './builder';
export type { WeaponRig } from './designs';

/** Weapon models use 2.5 cm voxels; the barrel points toward -Z (z = 0 is the muzzle end). */
export const WEAPON_VOXEL = 0.025;

export interface VoxelModelPart {
  /** The part's voxels (same dimensions/layout as the full grid). */
  grid: Uint8Array;
  /** Pivot / attachment point in voxel coordinates. */
  pivot: V3;
  /** Direction the part leaves the weapon during reloads (unit vector, model space). */
  dir: V3;
  /** Rotation axis for spinning parts. */
  axis: 'x' | 'y' | 'z';
}

export interface VoxelModel {
  sx: number;
  sy: number;
  sz: number;
  /** The complete model (static body plus every part at rest). Treat as read-only. */
  grid: Uint8Array;
  /** Colors indexed by grid value. */
  palette: number[];
  /** Grid values that glow. */
  emissive: number[];
  /** Hand grip point and muzzle point, in voxel coordinates. */
  grip: [number, number, number];
  muzzle: [number, number, number];
  /** Support-hand point (handguard / foregrip), null for one-handed weapons. */
  fore: [number, number, number] | null;
  /** A point on the sight line; aiming looks along -Z through it. */
  sight: [number, number, number];
  /** View-model hints: holding pose, reload style, per-shot action, melee swing. */
  rig: WeaponRig;
  /** Animated sub-parts (magazines, slides, pumps, bolts, cylinders, spinning barrels). */
  parts: Partial<Record<WeaponPartName, VoxelModelPart>>;
  /** Design id used for this model (a weapon id). */
  design: string;
}

interface BaseModel {
  sx: number;
  sy: number;
  sz: number;
  grid: Uint8Array;
  parts: Map<WeaponPartName, Uint8Array>;
  meta: DesignMeta;
}

const baseCache = new Map<string, BaseModel>();

function buildBase(design: string): BaseModel {
  let hit = baseCache.get(design);
  if (hit) return hit;
  const d = DESIGNS[design] ?? DESIGNS.brick_rifle;
  const [sx, sy, sz] = d.size;
  const b = new ModelBuilder(sx, sy, sz);
  const meta = d.build(b);
  hit = { sx, sy, sz, grid: b.grid, parts: b.parts, meta };
  baseCache.set(design, hit);
  return hit;
}

const specKey = (s: WeaponModelSpec): string => `${s.archetype}:${s.primary}:${s.secondary}:${s.accent}:${s.glow ?? -1}`;
let designBySpec: Map<string, string> | null = null;

/**
 * Design used for a spec. Weapons sharing an archetype get their own look: the spec is matched
 * back to its weapon in the catalog; unknown specs fall back to the archetype's default design.
 */
export function weaponDesignId(spec: WeaponModelSpec): string {
  if (!designBySpec) {
    designBySpec = new Map();
    for (const w of WEAPONS) if (DESIGNS[w.id]) designBySpec.set(specKey(w.model), w.id);
  }
  const id = designBySpec.get(specKey(spec));
  if (id) return id;
  return ARCHETYPE_DESIGN[spec.archetype] ?? 'brick_rifle';
}

function basePalette(spec: WeaponModelSpec): number[] {
  const glow = spec.glow ?? spec.accent;
  const p = new Array<number>(SLOT_COUNT).fill(0);
  p[PRI] = spec.primary;
  p[SEC] = spec.secondary;
  p[ACC] = spec.accent;
  p[GLOW] = glow;
  p[DARK] = 0x2a2a30;
  p[LIGHT] = tone(spec.primary, 1.24);
  p[METAL] = 0xa2aab5;
  p[PRI_D] = tone(spec.primary, 0.72);
  p[SEC_L] = tone(spec.secondary, 1.22);
  p[SEC_D] = tone(spec.secondary, 0.72);
  p[ACC_L] = tone(spec.accent, 1.22);
  p[ACC_D] = tone(spec.accent, 0.72);
  p[METAL_L] = 0xd6dce4;
  p[METAL_D] = 0x666d78;
  p[DARK_L] = 0x44454d;
  p[LENS] = 0x1d3a5c;
  p[LENS_HI] = 0xa8e4ff;
  p[BRASS] = 0xd9a441;
  p[RED] = 0xff3b30;
  p[GLOW_HI] = mix(glow, 0xffffff, 0.55);
  p[WHITE] = 0xeef0f2;
  p[RUBBER] = 0x18181c;
  return p;
}

function resolveCamo(camo: CamoDef | string | null | undefined): CamoDef | null {
  const c = typeof camo === 'string' ? CAMO_BY_ID[camo] : camo;
  return camoActive(c) ? c : null;
}

const cache = new Map<string, VoxelModel>();

/**
 * Builds (and caches) a weapon's voxel model. `camo` (a CamoDef or camo id) recolors the
 * primary/secondary/accent surfaces with the camo's colors and pattern; the factory finish
 * ('none') and unknown ids leave the model unchanged.
 */
export function buildWeaponModel(spec: WeaponModelSpec, camo?: CamoDef | string | null): VoxelModel {
  const c = resolveCamo(camo);
  const design = weaponDesignId(spec);
  const key = `${design}|${specKey(spec)}|${c?.id ?? ''}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const base = buildBase(design);
  const { sx, sy, sz, meta } = base;
  let grid = base.grid;
  let palette = basePalette(spec);
  let emissive = EMISSIVE_SLOTS.slice();
  if (c) {
    const res = applyCamo(base.grid, sx, sy, sz, palette, emissive, c, hashString(c.id) & 0xffff);
    grid = res.grid;
    palette = res.palette;
    emissive = res.emissive;
    if (c.glow) palette[GLOW] = c.glow;
    palette[GLOW_HI] = mix(palette[GLOW], 0xffffff, 0.55);
  }
  const parts: VoxelModel['parts'] = {};
  for (const [name, pg] of base.parts) {
    const pm = meta.parts?.[name];
    const g = new Uint8Array(pg.length);
    for (let i = 0; i < pg.length; i++) if (pg[i]) g[i] = grid[i];
    const dir = pm?.dir ?? [0, -1, 0];
    const len = Math.hypot(dir[0], dir[1], dir[2]) || 1;
    parts[name] = { grid: g, pivot: pm?.pivot ?? meta.grip, dir: [dir[0] / len, dir[1] / len, dir[2] / len], axis: pm?.axis ?? 'z' };
  }
  const model: VoxelModel = {
    sx,
    sy,
    sz,
    grid,
    palette,
    emissive,
    grip: meta.grip,
    muzzle: meta.muzzle,
    fore: meta.fore,
    sight: meta.sight,
    rig: meta.rig,
    parts,
    design,
  };
  cache.set(key, model);
  return model;
}
